import { transformSearch } from "@/api/helper.ts";
import { queryClient } from "@/query-client.ts";
import { queryOptions, useMutation } from "@tanstack/react-query";
import { z } from "zod";
import type { SiteDocumentation } from "../../types/site-documentation";
import siteDocumentationService from "../services/site-documentation-service";

export const siteDocumentationSearchSchema = z.object({
	search: z.string().optional(),
	site_id: z.string().optional(),
	category: z.string().optional(),
	created_by: z.string().optional(),
	documentation_date_from: z.string().optional(),
	documentation_date_to: z.string().optional(),
	page: z.number().nullable().optional(),
	per_page: z.number().optional(),
});

export const siteDocumentationsQueryOptions = (search?: z.infer<typeof siteDocumentationSearchSchema>, options?: { enabled: boolean }) => {
	return queryOptions({
		queryKey: ["site-documentations", search],
		queryFn: () =>
			siteDocumentationService.list(
				transformSearch(search, ["search", "site_id", "category", "created_by", "documentation_date_from", "documentation_date_to"]),
			),
		...options,
	});
};

export const siteDocumentationQueryOptions = (id: string) => {
	return queryOptions({
		queryKey: ["site-documentation", id],
		queryFn: () => siteDocumentationService.findById(id),
	});
};

export const useSiteDocumentationCreateMutation = () => {
	return useMutation({
		mutationFn: (siteDocumentation: Omit<SiteDocumentation, "id" | "created_at" | "updated_at" | "completion_percentage" | "site" | "creator">) =>
			siteDocumentationService.create(siteDocumentation),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["site-documentations"],
			});
		},
	});
};

export const useSiteDocumentationUpdateMutation = (id: string) => {
	return useMutation({
		mutationFn: (siteDocumentation: Partial<SiteDocumentation>) => siteDocumentationService.updateById(id, siteDocumentation),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["site-documentations"],
			});
			queryClient.invalidateQueries({
				queryKey: ["site-documentation", id],
			});
		},
	});
};

export const useSiteDocumentationDeleteMutation = () => {
	return useMutation({
		mutationFn: (id: string) => siteDocumentationService.deleteById(id),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["site-documentations"],
			});
		},
	});
};
